<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useLogger } from '@/composables/useLogger'

const logger = useLogger('FaqView');

const faqs = ref([])
const loading = ref(true)
const openId = ref(null)
const api_base_url = import.meta.env.VITE_API_BASE_URL

const toggle = (id) => {
  openId.value = openId.value === id ? null : id
}

onMounted(async () => {
  try {
    const { data } = await axios.get(`${api_base_url}/api/faqs/`)
    faqs.value = data
  } catch (e) {
    logger.error('Error fetching FAQs', e)
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="bg-theme-bg min-h-screen">
    <div class="max-w-3xl mx-auto px-4 pt-12 pb-16 sm:pt-16 sm:pb-20">
      <!-- Header -->
      <div class="text-center mb-10">
        <span class="inline-flex items-center gap-1.5 bg-primary-50 dark:bg-primary-600/10 text-primary-600 dark:text-primary-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
          <i class="pi pi-question-circle text-[10px]"></i> FAQ
        </span>
        <h1 class="text-3xl sm:text-4xl font-extrabold text-theme-text tracking-tight mb-3">
          Frequently Asked Questions
        </h1>
        <p class="text-base text-theme-text2 max-w-xl mx-auto">
          Can't find what you're looking for? <RouterLink to="/contact" class="text-primary-600 hover:underline font-medium">Contact us</RouterLink>.
        </p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="space-y-4">
        <div v-for="n in 4" :key="n" class="bg-theme-card border border-theme-border rounded-2xl p-6 animate-pulse">
          <div class="h-5 bg-theme-bg2 rounded w-3/4 mb-3"></div>
          <div class="h-3 bg-theme-bg2 rounded w-1/2"></div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="faqs.length === 0" class="bg-theme-card border border-theme-border rounded-2xl p-10 text-center">
        <i class="pi pi-inbox text-4xl text-theme-muted mb-3"></i>
        <p class="text-theme-text2">No FAQs available yet. Check back soon!</p>
      </div>

      <!-- Accordion -->
      <div v-else class="space-y-3">
        <div v-for="faq in faqs" :key="faq.id"
          class="bg-theme-card border border-theme-border rounded-2xl overflow-hidden transition-shadow"
          :class="{ 'shadow-md': openId === faq.id }">
          <button @click="toggle(faq.id)"
            class="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-theme-bg2/50 transition-colors">
            <span class="text-base font-semibold text-theme-text">{{ faq.question }}</span>
            <i class="pi text-sm text-primary-600 shrink-0 transition-transform duration-200"
              :class="openId === faq.id ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
          </button>
          <div v-show="openId === faq.id" class="px-5 pb-5 -mt-1">
            <p class="text-sm text-theme-text2 leading-relaxed">{{ faq.answer }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
